import type { DataViewFilter } from 'components/DataView';
import { supplierSource, warehouseColumnSource } from 'data/entity-sources/sources';

import { dateFromString } from 'helpers/dateTimes';

import { SUPPLY_ORDER_STATES_LABELS } from './states';
import type { SupplyOrderRow } from './supplyOrderViews';

const STATE_OPTIONS = Object.entries(SUPPLY_ORDER_STATES_LABELS).map(([value, label]) => ({ value, label }));

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Le périmètre par défaut de la lecture : les achats rangés ou annulés d'il y a plus d'une semaine ne
 * sont plus du travail en cours. C'est la seule variable de la query — tout le reste est filtré ici.
 *
 * Elle est posée par DÉFAUT et pas seulement proposée : sans borne, `supplyOrders` rend toute la
 * collection (33 000 achats et ~130 Mo sur un dump de prod, contre ~200 dans la fenêtre d'une
 * semaine) à chaque ouverture de la page. Effacer le chip reste possible et remonte bien tout
 * l'historique — mais c'est alors un geste explicite, pas le comportement au premier rendu.
 */
export function getDefaultDisplayLimitDate(): string {
    return new Date(Date.now() - ONE_WEEK_MS).toISOString().slice(0, 10);
}

/** Livraison prévue dans le passé, et toujours pas reçue. */
function isDelayed({ dateDeliveryScheduled, dateDelivery }: SupplyOrderRow): boolean {
    if (!dateDeliveryScheduled || dateDelivery) {
        return false;
    }
    return dateFromString(dateDeliveryScheduled) < new Date();
}

/**
 * `accessor` présent ⇒ la `DataView` filtre les lignes déjà chargées.
 * `accessor` absent  ⇒ filtre SERVEUR : la valeur est remontée par `onFiltersChange` et devient une
 *                      variable de la query. Ici, seule la date limite change l'ensemble chargé.
 *
 * Les clés d'URL sont celles d'avant la `DataView` : les liens déjà partagés restent valides.
 */
export const SUPPLY_ORDER_FILTERS: DataViewFilter<SupplyOrderRow>[] = [
    {
        // Borne BASSE des achats rangés/annulés, et rien d'autre : les achats en cours remontent
        // toujours. Effacer le chip retire la borne et fait donc remonter tout l'historique — ça se
        // voit, mais ça se demande : la valeur par défaut est là pour que le premier rendu ne lise
        // pas la collection entière.
        key: 'displayLimitDate',
        title: 'Date limite rangés/annulés',
        type: 'date',
        defaultValue: getDefaultDisplayLimitDate(),
    },
    {
        key: 'supplier',
        title: 'Fournisseur',
        type: 'async-multi-select',
        source: supplierSource,
        accessor: (supplyOrder) => supplyOrder.supplier._id,
    },
    {
        key: 'warehouseColumnId',
        title: 'Entrepôt',
        type: 'async-multi-select',
        source: warehouseColumnSource,
        accessor: (supplyOrder) => supplyOrder.warehouseColumn?._id,
    },
    {
        key: 'state',
        title: 'État',
        type: 'multi-select',
        options: STATE_OPTIONS,
        accessor: (supplyOrder) => supplyOrder.state,
    },
    {
        key: 'filterDelayed',
        title: 'En retard',
        type: 'boolean',
        accessor: isDelayed,
    },
];
